import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Size, lineDiscountOf, t, type ChangeOrderAnswer, type SalesOrderAnswer, documentName, newDocumentTitle } from '@bms2/shared';
import { DatePicker, Textarea } from '../../components/controls';
import { useDocumentToday } from '../../shell/ViewerZone';
import { discountFieldsOf, discountLevelOf } from './lineDiscountFields';
import { ItemTable, storedId, hasUnnamedDivider, newLine, savedRows, withDividers, type ItemLineDraft } from '../../components/composites/ItemTable';
import { ModalField } from '../../components/modals/ModalShell';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { CHANGE_ORDERS_PATH, SALES_ORDERS_PATH, post, put, taxCategorySearch, useApi } from './api';
import { useItemPicker } from '../settings/api';
import { previewLinesOf, useTaxPreview } from './useTaxPreview';

/** 🔨 A row the grid added carries no stored id; the save posts it as a new line rather than as an edit of one (decision 0445). */

/**
 * 🔒 The Change Order's edit mode (decision 0475), on the change order's own
 * page and never in a modal. The header is the reason and the description,
 * both required, and the lines follow on the Item Table's ChangeOrder column
 * set. The one footer control saves and routes: a change order has no outward
 * move of its own, because raising it is what sends it to the gate.
 *
 * 🔨 A line quantity below zero is a removal and the screen lets it through
 * (decision 0123). Only a quantity of zero is dropped, because zero moves
 * nothing and the server refuses it by name.
 */
export function ChangeOrderForm({ changeOrderId = null, apiBaseUrl = '' }: { changeOrderId?: string | null; apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const askedOrderId = params.get('salesOrderId');
  const stored = useApi<ChangeOrderAnswer>(changeOrderId ? `${CHANGE_ORDERS_PATH}/${changeOrderId}` : null, apiBaseUrl);
  const held = stored.state.kind === 'ready' ? stored.state.data : null;
  const salesOrderId = held?.salesOrderId ?? askedOrderId ?? '';
  const parent = useApi<SalesOrderAnswer>(salesOrderId ? `${SALES_ORDERS_PATH}/${salesOrderId}` : null, apiBaseUrl);
  const order = parent.state.kind === 'ready' ? parent.state.data : null;

  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // 🔒 The change order's own date (T114; decision 0681): its tax is the rate in force on it.
  // 🔒 A new change order opens on today in the viewer's own zone (decision 1030).
  const today = useDocumentToday();
  const [documentDate, setDocumentDate] = useState(today);
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 🔒 A picked Item brings its SKU and its sales description, as the purchase side's lines always did (T121; piece 4, the SKU under the name; decision 0303).

  // The stored change order arriving after the form opened fills the fields nobody has touched.
  const [seen, setSeen] = useState<ChangeOrderAnswer | null>(null);
  if (held && seen !== held) {
    setSeen(held);
    setReason(held.reason);
    setDescription(held.description);
    setDocumentDate(held.documentDate);
    setCustomerNotes(held.customerNotes ?? '');
    // 🔨 The stored category reopens with the line; a rewrite without it re-defaults the line to the Item's (decision 0435).
    setDrafts(
      held.lines.length === 0
        ? [newLine()]
        : // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
          withDividers(held.lines.map((row) => ({ id: row.id, itemId: row.itemId, itemLabel: row.itemName ?? row.description, sku: row.sku, description: row.description, qty: row.quantity, unit: row.unitName, rateCents: row.unitPriceCents, taxCategoryId: row.taxCategoryId, taxLabel: row.taxCategoryName, freeText: row.itemId === null, discount: lineDiscountOf(row.discountType, row.discountValue) })), held.dividers),
    );
  }
  const { unitIds, pickerProps } = useItemPicker(apiBaseUrl, 'sales');

  const taxSearch = useMemo(() => taxCategorySearch(apiBaseUrl), [apiBaseUrl]);

  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const saveRows = () =>
    savedRows(
      drafts,
      (d) => Boolean(d.itemId || d.itemLabel.trim() || d.description.trim()) && (d.qty ?? 0) !== 0,
      (d) => ({
        id: storedId(d),
        itemId: d.itemId,
        description: (d.freeText ? d.itemLabel : d.description).trim() || null,
        quantity: d.qty ?? 0,
        // The unit the grid's closed list wrote wins over the Item's own, which is what an override means (decision 0439).
        unitId: d.unitId ?? (d.itemId ? unitIds.current.get(d.itemId) ?? null : null),
        unitPriceCents: d.rateCents,
        taxCategoryId: d.taxCategoryId ?? held?.lines.find((row) => row.id === d.id)?.taxCategoryId ?? null,
        // 🔒 The discount the line carries goes back with it (decision 1031).
        ...discountFieldsOf(d.discount),
      }),
    );

  // 🔒 The tax is worked out live off the parent order's Ship-To, through the call the save makes (decisions 0644, 0645).
  const preview = useTaxPreview(
    salesOrderId ? { documentType: 'ChangeOrder', documentId: changeOrderId, salesOrderId, documentDate: documentDate || null, lines: previewLinesOf(drafts, (d) => ({ taxCategoryId: d.taxCategoryId ?? held?.lines.find((row) => row.id === d.id)?.taxCategoryId ?? null, ...discountFieldsOf(d.discount) })) } : null,
    apiBaseUrl,
  );

  const save = async (): Promise<void> => {
    if (!salesOrderId) return setError(t('changeOrder.needsOrder'));
    if (changeOrderId && !held) return setError(t('changeOrder.readFailed'));
    if (!reason.trim()) return setError(t('changeOrder.needsReason'));
    if (!description.trim()) return setError(t('changeOrder.needsDescription'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    const { lines, dividers } = saveRows();
    if (lines.length === 0) return setError(t('changeOrder.needsLines'));
    setBusy(true);
    const body = { reason: reason.trim(), description: description.trim(), documentDate: documentDate || null, customerNotes, lines, dividers };
    const saved = changeOrderId
      ? await put<{ id: string }>(`${CHANGE_ORDERS_PATH}/${changeOrderId}`, body, apiBaseUrl)
      : await post<{ id: string }>(`${SALES_ORDERS_PATH}/${salesOrderId}/change-orders`, body, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    navigate(`${CHANGE_ORDERS_PATH}/${changeOrderId ?? saved.data.id}`);
  };

  return (
    <DocumentEditPage
      title={held?.changeOrderNumber ?? newDocumentTitle('ChangeOrder')}
      subtitle={order?.salesOrderNumber ?? documentName('ChangeOrder')}
      outwardLabel={t('changeOrder.save')}
      busy={busy}
      error={error ?? (stored.state.kind === 'failed' ? t('changeOrder.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveAndSend={() => void save()}
      onCancel={() => navigate(changeOrderId ? `${CHANGE_ORDERS_PATH}/${changeOrderId}` : salesOrderId ? `/sales-orders/${salesOrderId}` : '/sales-orders')}
      header={
        <>
          <ModalField label={t('changeOrder.fact.order')}>
            <span data-change-order-parent={salesOrderId}>{order?.salesOrderNumber ?? ''}</span>
          </ModalField>
          <ModalField label={t('changeOrder.fact.account')}>
            <span data-change-order-account={order?.companyId ?? ''}>{order?.companyName ?? ''}</span>
          </ModalField>
          <div data-change-order-field="date">
            <DatePicker size={Size.Small} label={t('document.edit.date')} value={documentDate} onChange={setDocumentDate} mode="date" name="changeOrderDate" />
          </div>
          <ModalField label={t('changeOrder.reason')} required>
            <Textarea size={Size.Large} label={t('changeOrder.reason')} labelHidden value={reason} onChange={setReason} name="reason" />
          </ModalField>
          <ModalField label={t('changeOrder.description')} required>
            <Textarea size={Size.Large} label={t('changeOrder.description')} labelHidden value={description} onChange={setDescription} name="description" />
          </ModalField>
          <p className={styles.hint}>{t('changeOrder.lines.hint')}</p>
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="changeOrder" />
        </>
      }
      lines={
        <ItemTable
          documentType="ChangeOrder"
          lines={drafts}
          onChange={setDrafts}
          // 🔒 The Discount column follows the Discount Scope setting, read off the order this change order amends (decisions 0651, 1031).
          discountLevel={discountLevelOf(order?.discountLevel)}
          {...pickerProps({ saleGuards: true })}
          taxSearch={taxSearch}
          // 🔨 The figure under the category is the calculation's own, worked out live and never typed (decisions 0435, 0644).
          taxAmountCentsOf={(draft) => preview.taxCentsOf(draft.id) ?? held?.lines.find((row) => row.id === draft.id)?.taxAmountCents ?? null}
          totals={preview}
          currencyCode={order?.currencyCode ?? 'USD'}
        />
      }
    />
  );
}
